export type TreeMutationMode =
  | 'normal'
  | 'integrity-improvement';

export type MandatoryTreeMutationKind =
  | 'insert-member'
  | 'update-member'
  | 'delete-member'
  | 'restore-member'
  | 'set-parents'
  | 'set-spouse'
  | 'claim-member'
  | 'release-claim';

export interface MandatoryTreeMember {
  id: string;
  parentIds?: string[] | null;
  spouseId?: string | null;
  claimedByUserId?: string | null;
  profileState?: string | null;
  approvalStatus?: string | null;
}

export interface MandatoryCanonicalTopology {
  generationByMemberId: Record<string, number>;
  branchPathByMemberId: Record<string, string>;
}

export interface MandatoryTreeMutationInput {
  before: MandatoryTreeMember[];
  after: MandatoryTreeMember[];
  viewerMemberId: string | null;
  proposedViewerMemberId?: string | null;
  affectedMemberIds: string[];
  mutationKind: MandatoryTreeMutationKind;
  beforeTopology: MandatoryCanonicalTopology;
  afterTopology: MandatoryCanonicalTopology;
  mode?: TreeMutationMode;
}

export interface TreeIntegrityMetrics {
  danglingParentEdges: string[];
  danglingSpouseEdges: string[];
  cycleMemberIds: string[];
  connectedMemberIds: string[];
}

export interface MandatoryTreeMutationDecision {
  allowed: boolean;
  blockers: string[];
  before: TreeIntegrityMetrics;
  after: TreeIntegrityMetrics;
}

function activeMembers(
  members: MandatoryTreeMember[],
): MandatoryTreeMember[] {
  return members.filter(
    member =>
      member.profileState !== 'deleted' &&
      member.approvalStatus !== 'rejected',
  );
}

function unique(values: readonly string[]): string[] {
  return [...new Set(values.filter(Boolean))].sort();
}

function buildMap(
  members: MandatoryTreeMember[],
): Map<string, MandatoryTreeMember> {
  return new Map(
    activeMembers(members).map(member => [
      member.id,
      member,
    ]),
  );
}

function collectDanglingParents(
  members: Map<string, MandatoryTreeMember>,
): string[] {
  const failures: string[] = [];

  for (const member of members.values()) {
    for (const parentId of unique(
      member.parentIds ?? [],
    )) {
      if (!members.has(parentId)) {
        failures.push(
          `${member.id}->${parentId}`,
        );
      }
    }
  }

  return failures.sort();
}

function collectDanglingSpouses(
  members: Map<string, MandatoryTreeMember>,
): string[] {
  const failures: string[] = [];

  for (const member of members.values()) {
    const spouseId = member.spouseId ?? null;

    if (spouseId && !members.has(spouseId)) {
      failures.push(
        `${member.id}->${spouseId}`,
      );
    }
  }

  return failures.sort();
}

function collectCycles(
  members: Map<string, MandatoryTreeMember>,
): string[] {
  const cycleMembers = new Set<string>();

  function visit(
    memberId: string,
    path: string[],
    pathSet: Set<string>,
  ): void {
    if (pathSet.has(memberId)) {
      const start = path.indexOf(memberId);

      for (const id of path.slice(start)) {
        cycleMembers.add(id);
      }

      cycleMembers.add(memberId);
      return;
    }

    const member = members.get(memberId);
    if (!member) return;

    const nextPath = [...path, memberId];
    const nextSet = new Set(pathSet);
    nextSet.add(memberId);

    for (const parentId of unique(
      member.parentIds ?? [],
    )) {
      if (members.has(parentId)) {
        visit(
          parentId,
          nextPath,
          nextSet,
        );
      }
    }
  }

  for (const memberId of members.keys()) {
    visit(memberId, [], new Set());
  }

  return [...cycleMembers].sort();
}

function connectedFromViewer(
  members: Map<string, MandatoryTreeMember>,
  viewerMemberId: string | null,
): string[] {
  if (
    !viewerMemberId ||
    !members.has(viewerMemberId)
  ) {
    return [];
  }

  const neighbors =
    new Map<string, Set<string>>();

  for (const id of members.keys()) {
    neighbors.set(id, new Set());
  }

  for (const member of members.values()) {
    for (const parentId of unique(
      member.parentIds ?? [],
    )) {
      if (!members.has(parentId)) continue;

      neighbors.get(member.id)?.add(parentId);
      neighbors.get(parentId)?.add(member.id);
    }

    const spouseId = member.spouseId ?? null;

    if (spouseId && members.has(spouseId)) {
      neighbors.get(member.id)?.add(spouseId);
      neighbors.get(spouseId)?.add(member.id);
    }
  }

  const visited = new Set<string>();
  const queue = [viewerMemberId];

  while (queue.length > 0) {
    const current = queue.shift()!;

    if (visited.has(current)) continue;

    visited.add(current);

    for (
      const next of
        neighbors.get(current) ?? []
    ) {
      if (!visited.has(next)) {
        queue.push(next);
      }
    }
  }

  return [...visited].sort();
}

export function inspectMandatoryTreeIntegrity(
  members: MandatoryTreeMember[],
  viewerMemberId: string | null,
): TreeIntegrityMetrics {
  const map = buildMap(members);

  return {
    danglingParentEdges:
      collectDanglingParents(map),
    danglingSpouseEdges:
      collectDanglingSpouses(map),
    cycleMemberIds:
      collectCycles(map),
    connectedMemberIds:
      connectedFromViewer(
        map,
        viewerMemberId,
      ),
  };
}

function subtract(
  before: readonly string[],
  after: readonly string[],
): string[] {
  const beforeSet = new Set(before);

  return after.filter(
    value => !beforeSet.has(value),
  );
}

function defectCount(
  metrics: TreeIntegrityMetrics,
): number {
  return (
    metrics.danglingParentEdges.length +
    metrics.danglingSpouseEdges.length +
    metrics.cycleMemberIds.length
  );
}

function compareUnaffectedTopology(
  input: MandatoryTreeMutationInput,
  beforeConnected: readonly string[],
  blockers: string[],
): void {
  const affected =
    new Set(input.affectedMemberIds);

  for (const memberId of beforeConnected) {
    if (affected.has(memberId)) continue;

    const beforeGeneration =
      input.beforeTopology
        .generationByMemberId[memberId];

    const afterGeneration =
      input.afterTopology
        .generationByMemberId[memberId];

    if (
      beforeGeneration !== afterGeneration
    ) {
      blockers.push(
        `unaffected-generation-change:${memberId}:${beforeGeneration}->${afterGeneration}`,
      );
    }

    const beforeBranch =
      input.beforeTopology
        .branchPathByMemberId[memberId];

    const afterBranch =
      input.afterTopology
        .branchPathByMemberId[memberId];

    if (beforeBranch !== afterBranch) {
      blockers.push(
        `unaffected-branch-path-change:${memberId}:${beforeBranch ?? 'missing'}->${afterBranch ?? 'missing'}`,
      );
    }
  }
}

export function authorizeMandatoryTreeMutation(
  input: MandatoryTreeMutationInput,
): MandatoryTreeMutationDecision {
  const mode = input.mode ?? 'normal';

  const proposedViewer =
    input.proposedViewerMemberId === undefined
      ? input.viewerMemberId
      : input.proposedViewerMemberId;

  const before =
    inspectMandatoryTreeIntegrity(
      input.before,
      input.viewerMemberId,
    );

  const after =
    inspectMandatoryTreeIntegrity(
      input.after,
      proposedViewer,
    );

  const blockers: string[] = [];
  const afterMap = buildMap(input.after);

  const controlledClaim =
    input.mutationKind === 'claim-member' &&
    input.viewerMemberId === null &&
    proposedViewer !== null;

  const controlledRelease =
    input.mutationKind === 'release-claim' &&
    input.viewerMemberId !== null &&
    proposedViewer === null;

  if (
    input.viewerMemberId === null &&
    !controlledClaim
  ) {
    blockers.push(
      `viewer-anchor-required:${input.mutationKind}`,
    );
  }

  if (
    proposedViewer &&
    !afterMap.has(proposedViewer)
  ) {
    blockers.push(
      `missing-viewer-anchor:${proposedViewer}`,
    );
  }

  for (
    const edge of subtract(
      before.danglingParentEdges,
      after.danglingParentEdges,
    )
  ) {
    blockers.push(
      `new-dangling-parent:${edge}`,
    );
  }

  for (
    const edge of subtract(
      before.danglingSpouseEdges,
      after.danglingSpouseEdges,
    )
  ) {
    blockers.push(
      `new-dangling-spouse:${edge}`,
    );
  }

  for (
    const memberId of subtract(
      before.cycleMemberIds,
      after.cycleMemberIds,
    )
  ) {
    blockers.push(
      `new-ancestry-cycle:${memberId}`,
    );
  }

  const beforeConnected =
    before.connectedMemberIds;

  const afterConnected =
    new Set(after.connectedMemberIds);

  if (!controlledRelease) {
    for (const memberId of beforeConnected) {
      if (!afterConnected.has(memberId)) {
        blockers.push(
          `lost-connected-member:${memberId}`,
        );
      }
    }
  }

  if (
    input.viewerMemberId !== null &&
    !controlledRelease
  ) {
    compareUnaffectedTopology(
      input,
      beforeConnected,
      blockers,
    );
  }

  if (mode === 'integrity-improvement') {
    if (
      input.mutationKind !==
      'restore-member'
    ) {
      blockers.push(
        `integrity-improvement-not-authorized:${input.mutationKind}`,
      );
    }

    const beforeDefects =
      defectCount(before);

    const afterDefects =
      defectCount(after);

    const beforeConnectedSet =
      new Set(before.connectedMemberIds);

    const gainedConnected =
      after.connectedMemberIds.filter(
        memberId =>
          !beforeConnectedSet.has(memberId),
      );

    const reducedDefects =
      afterDefects < beforeDefects;

    const recoveredConnection =
      gainedConnected.length > 0;

    if (
      after.danglingParentEdges.length >
        before.danglingParentEdges.length ||
      after.danglingSpouseEdges.length >
        before.danglingSpouseEdges.length ||
      after.cycleMemberIds.length >
        before.cycleMemberIds.length
    ) {
      blockers.push(
        'repair-traded-one-defect-for-another',
      );
    }

    if (
      !reducedDefects &&
      !recoveredConnection
    ) {
      blockers.push(
        `repair-did-not-improve:${beforeDefects}->${afterDefects}`,
      );
    }
  }

  return {
    allowed: blockers.length === 0,
    blockers: unique(blockers),
    before,
    after,
  };
}
